import type { BaseModelAttributes } from './base';
import { Resolution, AspectRatio, I18nLabels } from './common';
import { TaskStatus, TaskStatusLabels } from './taskStatus';
import { Locale } from './locale';

// ⭐ 统一使用 TaskStatus，保留类型别名以向后兼容
export type ImageGenerationStatus = TaskStatus;
export const ImageGenerationStatus = TaskStatus;

export enum ImageGenerationType {
  GENERATION = 'generation',
  EDIT = 'edit'
}

// ⭐ 使用统一的状态标签，可自定义特定状态的显示文本
export const ImageGenerationStatusLabels: I18nLabels<ImageGenerationStatus> = {
  ...TaskStatusLabels,
  [TaskStatus.PROCESSING]: { [Locale.ZH_CN]: '生成中', [Locale.EN_US]: 'Generating' },
};

export interface IImageGeneration extends BaseModelAttributes {
  prompt: string;
  negativePrompt?: string;  // 新增：负面提示词
  modelId: string;
  resolution: Resolution;
  aspectRatio: AspectRatio;
  status: ImageGenerationStatus;
  imageType: ImageGenerationType;
  taskId?: string;
  resultMessage?: string;
  resultImageUrl?: string;
  referenceImageUrl?: string;
  referenceImages?: string[];
  submittedAt: Date;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateImageGenerationRequest {
  prompt: string;
  negativePrompt?: string;  // 新增：负面提示词
  modelId: string;
  resolution: Resolution;
  aspectRatio: AspectRatio;
  imageType: ImageGenerationType;
  referenceImageUrl?: string;
  referenceImages?: string[];
}

export interface UpdateImageGenerationRequest {
  prompt?: string;
  negativePrompt?: string;  // 新增：负面提示词
  modelId?: string;
  resolution?: Resolution;
  aspectRatio?: AspectRatio;
  status?: ImageGenerationStatus;
  imageType?: ImageGenerationType;
  taskId?: string;
  resultMessage?: string;
  resultImageUrl?: string;
  referenceImageUrl?: string;
  referenceImages?: string[];
}


export interface ImageGenerationQuery {
  search?: string;
  userId?: string;
  status?: ImageGenerationStatus;
  modelId?: string;
  imageType?: ImageGenerationType;
}

/**
 * 主体图生成请求（文生图）
 * 用于统一的主体图片生成 API
 */
export interface CreateSubjectImageGenerationRequest {
  subjectType: string;          // 主体类型（character/prop/scene），使用 string 避免循环依赖
  subjectName: string;          // 主体名称
  subjectDescription?: string;  // 主体描述
  userPrompt?: string;          // 用户自定义提示词
  artStyle?: string;            // 艺术风格
  modelId: string;              // 模型 ID
  aspectRatio?: AspectRatio;    // 宽高比（可选，默认根据类型决定）
}

/**
 * 主体图编辑请求（图生图）
 * 用于基于参考图生成主体设定图，支持布局修改和风格转换
 */
export interface CreateSubjectImageEditRequest {
  subjectType: string;          // 主体类型（character/prop/scene），使用 string 避免循环依赖
  subjectName: string;          // 主体名称
  subjectDescription?: string;  // 主体描述（可选）
  userPrompt?: string;          // 用户自定义提示词（可选）
  artStyle?: string;            // 艺术风格（可选）- 用于风格转换
  modelId: string;              // 模型 ID
  referenceImageUrls: string[]; // 参考图片 URL 数组（必填）
  aspectRatio?: AspectRatio;    // 宽高比（可选，默认 1:1）
}
